import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Info, Shield, Eye, Users, CheckCircle2, HeartHandshake } from 'lucide-react';
import AuthForm from '../components/AuthForm';
import Header from '../components/Header';
import LockerDetails from '../components/LockerDetails';
import NomineeLogin from '../components/NomineeLogin';
import Button from '../components/elements/Button';
import Notification from '../components/Notification';

const AuthPage = () => {
    const [showNominee, setShowNominee] = useState(false);
    const [isAuthLoginMode, setIsAuthLoginMode] = useState(true);
    const [searchParams] = useSearchParams();
    const [subscriptionKey] = useState(searchParams.get('key') || '');
    const navigate = useNavigate();

    // A page that sends someone here can hand over a message to show them, via
    // navigate('/', { state: { message } }). Account closure is the case that needs it:
    // confirming it ends every session including the tab that asked, so the user is
    // bounced to this page and the only chance to tell them what just happened - and that
    // they can sign back in before the erasure date to undo it - is here. Without this the
    // server's confirmation was being passed in and silently dropped.
    const location = useLocation();
    const [handoffMessage, setHandoffMessage] = useState(location.state?.message || '');

    useEffect(() => {
        document.title = "Eternal Locker™";
        if (window.location.pathname === '/nominee') {
            setShowNominee(true);
        }
    }, []);

    // Handler for successful login
    const handleAuthSuccess = () => {
        if (subscriptionKey) {
            navigate(`/subscription-check?key=${subscriptionKey}`);
            return;
        }
        navigate('/subscription-check');
    };

    return (
        <>
            <Notification
                message={handoffMessage}
                type="info"
                duration={15000}
                onClose={() => setHandoffMessage('')}
            />
            <Header showAccountDropdown={false} />
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-6 gap-10 items-start bg-background-dark font-sans">
                {/* Marketing Content (Visible on large screens) */}
                <div className={`lg:order-1 order-2 ${isAuthLoginMode ? 'lg:col-span-4' : 'lg:col-span-3'} col-span-1 h-full`}>
                    <LockerDetails />
                </div>
                {/* Auth Form or Nominee Login */}
                <div className={`lg:order-2 order-1 ${isAuthLoginMode ? 'lg:col-span-2' : 'lg:col-span-3'} col-span-1 flex flex-col items-center bg-card-background shadow-2xl rounded-xl border border-accent-main/30 w-full h-full pb-5`}>
                    {!showNominee ? (
                        <>
                            <AuthForm onAuthSuccess={handleAuthSuccess} onAuthModeChange={setIsAuthLoginMode} />
                            {isAuthLoginMode && (
                                <div className="px-8 pb-4 w-full text-sm font-sans mt-4">
                                    <div className="rounded-lg border border-accent-main/30 bg-background-light p-3">
                                        <div className="flex items-start gap-2 mb-3 text-primary-dark">
                                            <Info className="w-4 h-4 text-accent-main flex-shrink-0 mt-0.5" />
                                            <p className="text-left">Were you invited as a nominee by a family member? Use nominee access to continue.</p>
                                        </div>
                                        <Button type="anchor" className="w-full" onClick={() => setShowNominee(true)}>
                                            Continue as Nominee
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <NomineeLogin onAuthSuccess={handleAuthSuccess} />
                            <div className="px-8 pb-4 w-full text-sm font-sans mt-4">
                                <div className="rounded-lg border border-accent-main/30 bg-background-light p-3">
                                    <div className="flex items-start gap-2 mb-3 text-primary-dark">
                                        <Info className="w-4 h-4 text-accent-main flex-shrink-0 mt-0.5" />
                                        <p className="text-left">Looking to access your own locker? Return to account owner login.</p>
                                    </div>
                                    <Button type="anchor" className="w-full" onClick={() => setShowNominee(false)}>
                                        I am the account owner
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="max-w-6xl mx-auto mt-6 p-6 bg-card-background shadow-xl rounded-xl border border-accent-main/30">
                <div className="mb-5">
                    <span className="inline-block text-xs font-semibold tracking-wide uppercase text-accent-main bg-accent-main/10 border border-accent-main/20 rounded-full px-3 py-1">
                        Security & Trust
                    </span>
                    <h3 className="text-2xl font-bold text-primary-dark mt-3">Why Families Trust Us</h3>
                    <p className="text-sm text-text-muted mt-1">Built with practical safeguards that protect your data and preserve your control.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="h-full rounded-xl border border-accent-main/20 bg-background-light p-4">
                        <div className="flex items-center gap-2 mb-2 text-primary-dark">
                            <Shield className="w-4 h-4 text-accent-main" />
                            <h4 className="font-semibold">Encrypted Protection</h4>
                        </div>
                        <p className="text-primary-dark">Your sensitive information is encrypted and protected with layered safeguards.</p>
                    </div>
                    <div className="h-full rounded-xl border border-accent-main/20 bg-background-light p-4">
                        <div className="flex items-center gap-2 mb-2 text-primary-dark">
                            <CheckCircle2 className="w-4 h-4 text-accent-main" />
                            <h4 className="font-semibold">2-Step Verification</h4>
                        </div>
                        <p className="text-primary-dark">Login verification adds an extra security check before account access is granted.</p>
                    </div>
                    <div className="h-full rounded-xl border border-accent-main/20 bg-background-light p-4">
                        <div className="flex items-center gap-2 mb-2 text-primary-dark">
                            <Shield className="w-4 h-4 text-accent-main" />
                            <h4 className="font-semibold">DPDP-Aligned</h4>
                        </div>
                        <p className="text-primary-dark">Designed with DPDP-focused privacy and data protection practices.</p>
                    </div>
                    <div className="h-full rounded-xl border border-accent-main/20 bg-background-light p-4">
                        <div className="flex items-center gap-2 mb-2 text-primary-dark">
                            <Eye className="w-4 h-4 text-accent-main" />
                            <h4 className="font-semibold">Read-Only Access</h4>
                        </div>
                        <p className="text-primary-dark">Nominees can view your information but cannot modify your data.</p>
                    </div>
                    <div className="h-full rounded-xl border border-accent-main/20 bg-background-light p-4">
                        <div className="flex items-center gap-2 mb-2 text-primary-dark">
                            <Users className="w-4 h-4 text-accent-main" />
                            <h4 className="font-semibold">Complete Control</h4>
                        </div>
                        <p className="text-primary-dark">You choose nominees and manage your account independently at all times.</p>
                    </div>
                    <div className="h-full rounded-xl border border-accent-main/20 bg-background-light p-4">
                        <div className="flex items-center gap-2 mb-2 text-primary-dark">
                            <Users className="w-4 h-4 text-accent-main" />
                            <h4 className="font-semibold">Nominee Continuity</h4>
                        </div>
                        <p className="text-primary-dark">If you become inactive, nominees can securely access essential information when needed.</p>
                    </div>
                </div>
            </div>
            <div className="max-w-6xl mx-auto mt-6 p-6 bg-card-background shadow-xl rounded-xl border border-accent-main/30">
                <div className="rounded-xl border border-accent-main/20 bg-background-light p-6 text-center">
                    <div className="flex justify-center mb-3">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent-main/10 border border-accent-main/20">
                            <HeartHandshake className="w-5 h-5 text-accent-main" />
                        </span>
                    </div>
                    <h3 className="text-2xl font-extrabold text-primary-dark leading-tight">Give Your Family Peace of Mind Today</h3>
                    <p className="text-sm text-text-muted mt-2">Protect your digital legacy with secure access, clear controls, and trusted continuity when it matters most.</p>
                </div>
            </div>
            
        </>
    );
}

export default AuthPage;